import React, { useEffect, useState } from "react";
import StandardModal, { StandardModalBody, StandardModalFooter } from '../../../../components/modales/StandardModal';
import { Box, Typography, TextField, Button, Checkbox, FormControlLabel, FormGroup, Divider } from "@mui/material";
import { Route } from "../../../../models/route";
import { ExpenseType } from "../../../../models/expense-type";
import { EmptyState } from '@routes-plan/ui';
import { useT } from '../../../../i18n';

interface RouteCashBoxModalProps {
  open: boolean;
  handleClose: () => void;
  route: Route | null;
  expenses: ExpenseType[];
  selectedExpenses: number[];
  onSelectedExpensesChange: (expenseIds: number[]) => void;
  setValue: (value: number, expenseId: number) => void;
  isEditable: boolean;
}

const RouteCashBoxModal: React.FC<RouteCashBoxModalProps> = ({
  open,
  handleClose,
  route,
  expenses,
  selectedExpenses,
  onSelectedExpensesChange,
  setValue,
  isEditable,
}) => {
  const t = useT();
  const [inputValues, setInputValues] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!open) {
      setInputValues({});
      return;
    }

    setInputValues(prev => {
      const updated: Record<number, string> = {};

      selectedExpenses.forEach(id => {
        if (prev[id] !== undefined) {
          updated[id] = prev[id];
          return;
        }

        const expense = expenses.find(exp => exp.id === id);
        if (expense && expense.value !== undefined && expense.value !== null && expense.value !== 0) {
          updated[id] = expense.value.toString();
        } else {
          updated[id] = "";
        }
      });

      const prevKeys = Object.keys(prev);
      const hasChanges =
        prevKeys.length !== selectedExpenses.length ||
        selectedExpenses.some(id => updated[id] !== prev[id]);

      return hasChanges ? updated : prev;
    });
  }, [open, selectedExpenses, expenses]);

  const getDisplayValue = (expense: ExpenseType) => {
    if (inputValues[expense.id] !== undefined) {
      return inputValues[expense.id];
    }

    if (expense.value !== undefined && expense.value !== null && expense.value !== 0) {
      return expense.value.toString();
    }

    return "";
  };

  const handleValueChange = (
    expenseId: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;

    setInputValues(prev => ({ ...prev, [expenseId]: value }));

    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      setValue(0, expenseId);
      return;
    }

    const parsedValue = Number(trimmedValue);
    if (!Number.isNaN(parsedValue) && parsedValue >= 0) {
      setValue(parsedValue, expenseId);
    }
  };

  return (
    <StandardModal
      open={open}
      onClose={handleClose}
      title={t('expenses.adjustBox')}
      subtitle={route?.name}
      maxWidth={500}
      composed
    >
      <StandardModalBody>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t('expenses.selectExpenses')}
        </Typography>
        <FormGroup>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(180px, 100%), 1fr))', columnGap: 1 }}>
            {expenses.map((expense) => (
              <Box key={expense.id} sx={{ minWidth: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedExpenses.includes(expense.id)}
                      onChange={() => {
                        const newSelectedExpenses = selectedExpenses.includes(expense.id)
                          ? selectedExpenses.filter((id) => id !== expense.id)
                          : [...selectedExpenses, expense.id];
                        onSelectedExpensesChange(newSelectedExpenses);
                      }}
                      disabled={!isEditable}
                    />
                  }
                  label={expense.name}
                />
              </Box>
            ))}
          </Box>
        </FormGroup>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {selectedExpenses.length > 0 ? (
            selectedExpenses.map((expenseId) => {
              const expense = expenses.find((exp) => exp.id === expenseId);
              if (!expense) return null;

              return (
                <Box key={expense.id} sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{expense.name}</Typography>
                  <Box>
                    <TextField
                      fullWidth
                      label={t('expenses.value')}
                      placeholder="1000"
                      type="number"
                      required
                      size="small"
                      value={getDisplayValue(expense)}
                      onChange={(event) => handleValueChange(expense.id, event)}
                      inputProps={{ maxLength: 20, min: 0 }}
                      disabled={!isEditable}
                    />
                  </Box>
                </Box>
              );
            })
          ) : (
            <EmptyState dense title={t('expenses.noExpensesSelected')} />
          )}
        </Box>

      </StandardModalBody>
      <StandardModalFooter>
        <Button variant="contained" onClick={handleClose}>
          {t('common.close')}
        </Button>

      </StandardModalFooter>
    </StandardModal>
  );
};

export default RouteCashBoxModal;
